import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, CommitNumberInput, Input, MoneyInput, NumberInput, PageHeader, PageLoading, Segmented, Skeleton, Switch } from '@koyomi/ui';
import { api } from '../../lib/api.ts';
import { formatMoney, useBusiness, useRegions, useSave, useServices, type RegionDto } from '../../lib/queries.ts';

export function HomeServicePage() {
  const { t, i18n } = useTranslation(['config', 'common']);
  const navigate = useNavigate();
  const business = useBusiness();
  const regions = useRegions();
  const services = useServices();
  const [draft, setDraft] = useState<{ id?: string; name: string; travelTimeMin: string; fee: string } | null>(null);
  const saveSettings = useSave((body: Record<string, unknown>) => api('/business/settings', { method: 'PATCH', body: { homeService: body } }), ['business'], { silent: true });
  const saveRegion = useSave((r: { id?: string; name: string; travelTimeMin: number; fee: number | null; active: boolean }) => (r.id ? api(`/business/regions/${r.id}`, { method: 'PUT', body: r }) : api('/business/regions', { body: r })), ['regions']);
  const delRegion = useSave((id: string) => api(`/business/regions/${id}`, { method: 'DELETE' }), ['regions']);
  if (business.isLoading) return <><PageHeader title={t('config:home.title')} subtitle={t('config:home.subtitle')} /><PageLoading cards={2} /></>;
  if (!business.data) return null;
  const b = business.data;
  const hs = b.settings.homeService;
  const homeServices = (services.data ?? []).filter((s) => s.allowsHome && s.active);
  const edit = (r: RegionDto) => setDraft({ id: r.id, name: r.name, travelTimeMin: String(r.travelTimeMin), fee: r.fee == null ? '' : String(r.fee) });
  return (
    <>
      <PageHeader title={t('config:home.title')} subtitle={t('config:home.subtitle')} />
      {b.modality === 'LOCAL' && (
        <Card tone="warn" style={{ marginBottom: 20 }}>
          <div className="k-row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span>{t('config:home.offHint')}</span>
            <Button size="sm" onClick={() => navigate('/config/negocio')}>{t('config:home.goToBusiness')}</Button>
          </div>
        </Card>
      )}
      <div className="k-stack" style={{ marginBottom: 20 }}>
        <Segmented value={hs.mode} onChange={(mode) => saveSettings.mutate({ mode })} options={(['REGIONS', 'RADIUS'] as const).map((m) => ({ value: m, label: t(`config:home.modes.${m}`) }))} />
      </div>
      <div className="k-split">
        <div className="k-stack">
          {hs.mode === 'REGIONS' ? regions.isLoading ? <Card><Skeleton lines={4} /></Card> : (
            <div className="k-table__wrap">
              <table className="k-table">
                <thead><tr><th>{t('config:home.cols.region')}</th><th>{t('config:home.cols.travel')}</th><th>{t('config:home.cols.fee')}</th><th /></tr></thead>
                <tbody>
                  {(regions.data ?? []).map((r) => (
                    <tr key={r.id} onClick={() => edit(r)}>
                      <td data-label={t('config:home.cols.region')} style={{ fontWeight: 500, opacity: r.active ? 1 : 0.5 }}>{r.name}</td>
                      <td data-label={t('config:home.cols.travel')}>{r.travelTimeMin} {t('config:common.min')}</td>
                      <td data-label={t('config:home.cols.fee')}>{r.fee == null ? t('config:common.noFee') : formatMoney(r.fee, i18n.language)}</td>
                      <td style={{ textAlign: 'right' }}><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); if (confirm(t('config:common.confirmDelete'))) delRegion.mutate(r.id); }}>×</Button></td>
                    </tr>
                  ))}
                  <tr onClick={() => setDraft({ name: '', travelTimeMin: '30', fee: '' })}><td colSpan={4}><span className="k-linkbtn">+ {t('config:home.addRegion')}</span></td></tr>
                </tbody>
              </table>
            </div>
          ) : (
            <Card>
              <div className="k-stack">
                <CommitNumberInput label={t('config:home.radius')} maxDecimals={1} min={0} suffix={t('common:units.km')} value={hs.radiusKm ?? null} onCommit={(v) => saveSettings.mutate({ radiusKm: v })} hint={t('config:home.radiusHint')} />
              </div>
            </Card>
          )}
          <Card>
            <Switch label={t('config:home.requireFullAddress')} checked={hs.requireFullAddress} onChange={(v) => saveSettings.mutate({ requireFullAddress: v })} />
          </Card>
          <Card title={t('config:home.servicesHome')}>
            {services.isLoading ? <Skeleton lines={2} /> : <>
              <div className="k-checkchips">{homeServices.map((s) => <Chip key={s.id} tone="neutral">{s.name}</Chip>)}</div>
              <p className="k-muted" style={{ marginBottom: 0 }}>{homeServices.length ? t('config:home.servicesHomeHint') : t('config:home.noServicesHome')}</p>
            </>}
          </Card>
        </div>
        {draft ? (
          <Card className="k-panel" title={draft.id ? t('common:actions.edit') : t('config:home.addRegion')}>
            <form className="k-stack" onSubmit={(e) => { e.preventDefault(); saveRegion.mutate({ id: draft.id, name: draft.name, travelTimeMin: Number(draft.travelTimeMin), fee: draft.fee === '' ? null : Number(draft.fee), active: true }, { onSuccess: () => setDraft(null) }); }}>
              <Input label={t('config:home.regionName')} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus />
              <NumberInput label={t('config:home.travel')} suffix={t('config:common.min')} value={draft.travelTimeMin === '' ? null : Number(draft.travelTimeMin)} onChange={(v) => setDraft({ ...draft, travelTimeMin: v == null ? '' : String(v) })} />
              <MoneyInput label={t('config:home.fee')} locale={i18n.language} value={draft.fee === '' ? null : Number(draft.fee)} onChange={(v) => setDraft({ ...draft, fee: v == null ? '' : String(v) })} />
              <div className="k-row" style={{ justifyContent: 'flex-end' }}><Button type="button" onClick={() => setDraft(null)}>{t('common:actions.cancel')}</Button><Button type="submit" variant="primary" loading={saveRegion.isPending}>{t('common:actions.save')}</Button></div>
            </form>
          </Card>
        ) : (
          <div className="k-hatched k-panel" style={{ minHeight: 320, borderRadius: 'var(--radius-lg)', display: 'grid', placeItems: 'center' }}><span className="k-mono k-muted">{t('config:home.map')}</span></div>
        )}
      </div>
    </>
  );
}
